import { AuthPage as AuthPageComponent } from "@/components/auth";
import { useSignIn, useSignUp } from "@/packages/features-auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConvexAuth } from "convex/react";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/auth")({
  component: AuthRouteComponent,
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => ({
    returnTo: typeof search.returnTo === "string" ? search.returnTo : undefined,
  }),
});

/**
 * Route component for /auth — wraps AuthPage with returnTo redirect support.
 * After successful login, if returnTo is present in search params, navigate there.
 */
function AuthRouteComponent() {
  const { returnTo } = Route.useSearch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useConvexAuth();

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    if (returnTo) {
      navigate({ to: returnTo });
      return;
    }

    navigate({ to: "/" });
  }, [isAuthenticated, isLoading, returnTo, navigate]);

  return <AuthPage />;
}

type AuthFlow = "signIn" | "signUp" | "otp-verification";

/**
 * AuthPage — standalone auth form component.
 * Exported for use in __root.tsx as fallback for unauthenticated users.
 * Does NOT depend on route context (no Route.useSearch).
 */
export function AuthPage() {
  const [flow, setFlow] = useState<AuthFlow>("signIn");
  // Track which flow triggered OTP so we use the right handlers
  const [otpSource, setOtpSource] = useState<"signIn" | "signUp">("signUp");

  const {
    step: signInStep,
    signIn,
    verifyOtp: signInVerifyOtp,
    resendOtp: signInResendOtp,
    isLoading: signInIsLoading,
    error: signInError,
    verificationEmail: signInVerificationEmail,
    backToCredentials: signInBackToCredentials,
  } = useSignIn();

  const {
    step: signUpStep,
    signUp,
    verifyOtp: signUpVerifyOtp,
    resendOtp: signUpResendOtp,
    isLoading: signUpIsLoading,
    error: signUpError,
    password: signUpPassword,
    setPassword: setSignUpPassword,
    hasAttemptedSubmit: signUpHasAttemptedSubmit,
    verificationEmail: signUpVerificationEmail,
    backToCredentials: signUpBackToCredentials,
  } = useSignUp();

  // Sync sign-up step to flow
  useEffect(() => {
    if (signUpStep === "otp-verification") {
      setOtpSource("signUp");
      setFlow("otp-verification");
    }
  }, [signUpStep]);

  // Sync sign-in step to flow
  useEffect(() => {
    if (signInStep === "otp-verification") {
      setOtpSource("signIn");
      setFlow("otp-verification");
    }
  }, [signInStep]);

  const handleSignInSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    await signIn({ email, password });
  };

  const handleSignUpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirm-password") as string;
    const name = formData.get("name") as string | undefined;
    await signUp({ email, password, confirmPassword, name });
  };

  // Route OTP handlers based on which flow triggered it
  const otpVerifyOtp =
    otpSource === "signIn" ? signInVerifyOtp : signUpVerifyOtp;
  const otpResendOtp =
    otpSource === "signIn" ? signInResendOtp : signUpResendOtp;
  const otpIsLoading =
    otpSource === "signIn" ? signInIsLoading : signUpIsLoading;
  const otpError = otpSource === "signIn" ? signInError : signUpError;
  const otpVerificationEmail =
    otpSource === "signIn" ? signInVerificationEmail : signUpVerificationEmail;

  const handleOtpBack = () => {
    if (otpSource === "signIn") {
      signInBackToCredentials();
      setFlow("signIn");
    } else {
      signUpBackToCredentials();
      setFlow("signUp");
    }
  };

  return (
    <AuthPageComponent
      flow={flow}
      onFlowChange={setFlow}
      brandName="Auditera"
      onSignInSubmit={handleSignInSubmit}
      signInIsLoading={signInIsLoading}
      signInError={signInError}
      onSignUpSubmit={handleSignUpSubmit}
      signUpPassword={signUpPassword}
      onSignUpPasswordChange={setSignUpPassword}
      signUpIsLoading={signUpIsLoading}
      signUpError={signUpError}
      signUpHasAttemptedSubmit={signUpHasAttemptedSubmit}
      onOtpSubmit={otpVerifyOtp}
      onOtpResend={otpResendOtp}
      onOtpBack={handleOtpBack}
      otpIsLoading={otpIsLoading}
      otpError={otpError}
      verificationEmail={otpVerificationEmail}
    />
  );
}
